import {
  Component,
  OnDestroy,
  OnInit,
  ElementRef,
  ViewChild,
  signal,
  WritableSignal,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { zip } from 'rxjs';
import {
  BreadcrumbService,
  ActiveFilterService,
  PageOptions,
  SortOptions,
  HistogramBucket,
  FirestoreSettingsService,
  GridSortingService,
} from '@/app/core';
import { EquipmentService } from '@/app/equipment/equipment.service';
import { SelectedProductTypeService } from '@/app/equipment/product-type/selected-product-type.service';
import { ProductTypeBaseComponent } from '@/app/equipment/product-type/product-type-base.component';
import { Interval } from '@/app/models';
import { AuthenticationService } from '@/app/core/authentication/authentication.service';
import { FilterProfileService } from '@/app/filter/profiles/filter-profile.service';
import { CODENAMES } from '@/app/core/constants';
import throttle from 'lodash/throttle';

export class RateTypeData {
  clientBenchmarkedRevenue: number; // TODO: Figure out which spelling is correct.
  ClientBenchmarkedRevenue: number;
  benchmarkedRevenue: number;
  BenchmarkedRevenue: number; // TODO: Figure out which spelling is correct.
  RevenueTotal: number;
}

@Component({
  selector: 'rdo-product-type-rate-types',
  templateUrl: 'product-type-rate-types.component.html',
  styleUrls: ['./product-type-rate-types.component.scss'],
  standalone: false,
})
export class ProductTypeRateTypesComponent
  extends ProductTypeBaseComponent
  implements OnInit, OnDestroy
{
  @ViewChild('rateTypesScreen') rateTypesScreen: ElementRef;
  public chartWidth: WritableSignal<number> = signal(100);

  hourlyRateType: RateTypeData = new RateTypeData();
  dailyRateType: RateTypeData = new RateTypeData();
  weeklyRateType: RateTypeData = new RateTypeData();
  monthlyRateType: RateTypeData = new RateTypeData();

  monthlyRateTrend: Array<any>;
  weeklyRateTrend: Array<any>;
  dailyRateTrend: Array<any>;
  hourlyRateTrend: Array<any>;

  paging: PageOptions = new PageOptions();
  sorting: SortOptions; // todo: RDO-1119

  monthlyHistogramData: any;
  weeklyHistogramData: any;
  dailyHistogramData: any;
  hourlyHistogramData: any;

  public loading = true;
  private clientId: string;
  public showHourlyRate = true;
  public gridName: string = 'PRODUCT_TYPE_RATETYPE_GRID';
  public columnClass = signal('col-12 col-lg-6 col-xl-4');

  constructor(
    private equipmentService: EquipmentService,
    private router: Router,
    private route: ActivatedRoute,
    private activeFilterService: ActiveFilterService,
    selectedProductTypeService: SelectedProductTypeService,
    breadcrumbService: BreadcrumbService,
    private hostElement: ElementRef,
    private firestoreSettingsService: FirestoreSettingsService,
    private authenticationService: AuthenticationService,
    private gridSortingService: GridSortingService,
    private filterProfileService: FilterProfileService
  ) {
    super(selectedProductTypeService, breadcrumbService, activeFilterService);
    this.clientId =
      this.authenticationService._userInfoView.SelectedClient.ClientID.toString();
  }

  ngOnInit() {
    this.gridSortingService.setGridName(this.gridName);
    this.sorting = this.gridSortingService.getSortOptions();
    window.addEventListener('resize', this.onResize);

    this.subscriptions.push(
      this.route.parent.params.subscribe((params) => {
        const routeParams = this.getRouteParams(params);
        if (this.hasRouteChanged(this.params, routeParams)) {
          this.params = routeParams;
          this.doUpdateBreadcrumbs();
          this.load();
        }
      })
    );

    this.subscriptions.push(
      this.activeFilterService.filterChange.subscribe((_) => {
        this.hostElement.nativeElement.scrollIntoView({
          block: 'start',
          behavior: 'smooth',
        });
        this.load();
      })
    );

    this.subscriptions.push(
      this.selectedProductTypeService.productTypeChange.subscribe((p) => {
        this.doUpdateBreadcrumbs();

        // When useRentAsProductType is active then we have to use the Description property as the product type
        //  product types will not match in this case.
        const currentProductType = this.params.useRentedAsProductType
          ? p.Description
          : p.ProductType;
        if (this.params.productType !== currentProductType) {
          this.hostElement.nativeElement.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
          });
        }
        this.selectedProductTypeService.loading = true;
        this.params.productType = currentProductType;
        this.load();
      })
    );

    if (!this.selectedProductTypeService.selectedProductType) {
      this.subscriptions.push(
        this.selectedProductTypeService.productType.subscribe((p) => {
          this.doUpdateBreadcrumbs();
        })
      );
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    window.removeEventListener('resize', this.onResize);
    this.onResize.cancel?.();
  }

  private onResize = throttle(
    () => {
      this.chartWidth.set(this.calculateChartWidth());
    },
    500,
    { leading: false, trailing: true }
  );

  private doUpdateBreadcrumbs() {
    this.updateBreadcrumbs([
      {
        title: 'main.core.common.counts.rate_types.plural',
        class: 'active',
      },
    ]);
  }

  private load = () => {
    if (!this.params.productType) {
      this.loading = false;
      return;
    }
    this.loading = true;
    this.sorting.sortOn = this.gridSortingService.getSortFieldOrDefault(); // todo: RDO-1119
    this.sorting.descending =
      this.gridSortingService.getSortDirectionOrDefault() === -1;
    const histogramOptions = {
      mode: Interval.Monthly,
      productType: this.params.productType,
      category: this.params.category,
      useRentedAsProductType: this.params.useRentedAsProductType,
    };
    zip(
      this.equipmentService.getProductTypeRateTypes({
        productType: this.params.productType,
        category: this.params.category,
        useRentedAsProductType: this.params.useRentedAsProductType,
      }),

      /*
            { mode: Interval, productType: string, category?: string, useRentedAsProductType?: boolean }
            * */
      this.equipmentService.getHistogramData(histogramOptions),
      this.equipmentService.getHistogramData(
        Object.assign({}, histogramOptions, { mode: Interval.Weekly })
      ),
      this.equipmentService.getHistogramData(
        Object.assign({}, histogramOptions, { mode: Interval.Daily })
      ),
      this.equipmentService.getHistogramData(
        Object.assign({}, histogramOptions, { mode: Interval.Hourly })
      ),
      this.equipmentService.getProductTypeRateTrends(
        {
          productType: this.params.productType,
          category: this.params.category,
          useRentedAsProductType: this.params.useRentedAsProductType,
        },
        this.paging,
        this.sorting
      ),
      this.filterProfileService.waitForCurrentProfile()
    ).subscribe((results: [any, any, any, any, any, any, boolean]) => {
      this.monthlyRateType = results[0].Items[0];
      this.weeklyRateType = results[0].Items[2];
      this.dailyRateType = results[0].Items[3];
      this.hourlyRateType = results[0].Items[4] || {};

      this.monthlyHistogramData = results[1];
      this.weeklyHistogramData = results[2];
      this.dailyHistogramData = results[3];
      this.hourlyHistogramData = results[4];

      this.monthlyRateTrend = results[5].MonthlyRateTrend;
      this.weeklyRateTrend = results[5].WeeklyRateTrend;
      this.dailyRateTrend = results[5].DailyRateTrend;
      this.hourlyRateTrend = results[5].HourlyRateTrend;
      this.selectedProductTypeService.loading = false;
      this.loading = false;
      this.showHourlyRate =
        this.filterProfileService.readCurrentProfileProperty(
          CODENAMES.CN_SHOW_HOURLY_RATE
        ) !== false; // nulls are defaulted to true

      this.setColumnClass();
      setTimeout(() => {
        this.chartWidth.set(this.calculateChartWidth());
      }, 100);
    });
  };

  private setColumnClass() {
    if (this.showHourlyRate) {
      this.columnClass.set(`histogram-column col-12 col-lg-6 col-xl-3 mb-2`);
    } else {
      this.columnClass.set(`histogram-column col-12 col-lg-6 col-xl-4 mb-2`);
    }
  }

  /**
   * Calculates each chart's width based on the bounding rect of the first histogram element.
   */
  public calculateChartWidth(): number {
    const el = this.hostElement.nativeElement.querySelector('rdo-histogram');
    if (el) {
      return el.getBoundingClientRect().width;
    } else {
      return 100;
    }
  }

  public handleHistogramClick = (bucket: HistogramBucket, rateType: string) => {
    const rtPath = rateType.split('.');
    let navRateType = rtPath.length ? rtPath[rtPath.length - 1] : rateType;
    navRateType = navRateType.charAt(0).toUpperCase() + navRateType.slice(1);
    const oldParams = this.params.clone();
    const route = [
      '/equipment',
      'product-types',
      this.params.productType,
      oldParams,
      'transactions',
      {
        fromValue: bucket.from,
        toValue: bucket.to,
        rateType: navRateType,
      },
    ];

    const from = bucket.displayFrom.toLocaleString();
    const to = bucket.displayTo.toLocaleString();

    this.updateBreadcrumbs([
      {
        title: `${from} - ${to} ${rateType} main.core.common.counts.transactions.plural`,
        class: 'active',
      },
    ]);
    this.router.navigate(route);
  };
}
